import { useEffect, useMemo, useState } from "react";
import { api, type CharacterDraftSummary, type SessionCustomer, type SpeciesItem } from "./api.js";
import { speciesArtUrl } from "./artwork.js";
import { BuilderMechanical } from "./BuilderMechanical.js";
import { GuestBuilder } from "./GuestBuilder.js";

type LoadState = "loading" | "ready" | "error";
const QUICKSTART_ORDER = ["species.kappa", "species.kitsune", "species.oni", "species.yamabushi_tengu"];
const SHOPIFY_BUILDER_URL = "https://mycatportal.com/pages/yokai-character-builder";
const YOKAI_PROJECT_URL = "https://mycatportal.com/pages/yokai";
const LOGOUT_RETURN_COOKIE = "yokai_logout_return";

const SPECIES_TEASERS: Record<string, string> = {
  "species.kappa": "River tricksters, sumo fanatics, and ancient keepers of dangerous waters.",
  "species.kitsune": "Cunning shapeshifters whose beauty, fire, and illusions rarely come without a price.",
  "species.oni": "Fearsome beings of strength, appetite, fury, and surprisingly complicated loyalties.",
  "species.yamabushi_tengu": "Mountain ascetics, martial masters, and storm-bringers with very little patience.",
  "species.bakeneko": "Old cats transformed into eerie guardians, gluttons, spies, or something worse.",
  "species.inugami_jiyuu": "Freed dog spirits searching for a Clan worthy of their absolute loyalty.",
  "species.jorogumo": "Beautiful spider women who weave seduction, secrets, and golden traps.",
  "species.karakasa_kozo": "Ancient umbrellas that woke up, grew an eye, and developed opinions.",
  "species.medama": "Watchful living eyes that turn observation, omens, and discomfort into power.",
  "species.mizuchi": "Water dragons carrying the patience of rivers and the violence of floods.",
  "species.noppera_bo": "Faceless presences that turn familiarity, identity, and recognition into terror.",
  "species.raiju": "Lightning beasts born from thunder, speed, instinct, and divine catastrophe.",
  "species.rokurokubi": "Night wanderers whose bodies remain behind while their necks hunt through darkness.",
  "species.tanuki": "Shameless shapeshifters powered by appetite, tricks, luck, and spectacular bad ideas.",
  "species.yuki_onna": "Winter spirits of impossible beauty, freezing breath, and merciless silence.",
  "species.hanyo": "Half-yōkai who live between human expectations and preternatural inheritance.",
};

function currentDraftId(): string | null {
  const match = window.location.pathname.match(/^\/builder\/([0-9a-f-]{36})\/?$/i);
  return match?.[1] ?? null;
}

function currentGuestSpeciesId(): string | null {
  const match = window.location.pathname.match(/^\/guest-builder\/(species\.[a-z0-9_]+)\/?$/i);
  return match?.[1] ?? null;
}

function replaceTopLocation(url: string): void {
  try {
    if (window.top && window.top !== window) {
      window.top.location.replace(url);
      return;
    }
  } catch {
    // Fall back to the current context only when top-level navigation is unavailable.
  }
  window.location.replace(url);
}

function consumeShopifyLogoutReturn(): boolean {
  const shouldReturn = document.cookie
    .split(";")
    .map((part) => part.trim())
    .some((part) => part === `${LOGOUT_RETURN_COOKIE}=1`);

  if (!shouldReturn) return false;

  document.cookie = `${LOGOUT_RETURN_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0; Secure`;
  replaceTopLocation(SHOPIFY_BUILDER_URL);
  return true;
}

function TrashIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h10l-.75 11H7.75L7 9Zm3 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" /></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3h1a2 2 0 0 1 2 2v8H4v-8a2 2 0 0 1 2-2h1Zm2 0h6V7a3 3 0 0 0-6 0v3Zm3 3a2 2 0 0 0-1 3.73V18h2v-1.27A2 2 0 0 0 12 13Z" /></svg>;
}

function sortSpecies(items: SpeciesItem[]): SpeciesItem[] {
  return [...items].sort((a, b) => {
    const aQuick = QUICKSTART_ORDER.indexOf(a.id);
    const bQuick = QUICKSTART_ORDER.indexOf(b.id);
    if (aQuick >= 0 || bQuick >= 0) {
      if (aQuick < 0) return 1;
      if (bQuick < 0) return -1;
      return aQuick - bQuick;
    }
    return (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.english_name.localeCompare(b.english_name);
  });
}

export function App() {
  const draftId = currentDraftId();
  const guestSpeciesId = currentGuestSpeciesId();
  const [state, setState] = useState<LoadState>("loading");
  const [customer, setCustomer] = useState<SessionCustomer | null>(null);
  const [species, setSpecies] = useState<SpeciesItem[]>([]);
  const [drafts, setDrafts] = useState<CharacterDraftSummary[]>([]);
  const [busySpecies, setBusySpecies] = useState<string | null>(null);
  const [busyDraft, setBusyDraft] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const speciesNameById = useMemo(() => new Map(species.map((item) => [item.id, item.english_name])), [species]);
  const selectableCount = useMemo(() => species.filter((item) => item.is_selectable).length, [species]);
  const authenticated = Boolean(customer);

  useEffect(() => {
    if (consumeShopifyLogoutReturn()) return;
    void load();
  }, []);

  async function load() {
    setState("loading");
    setMessage(null);
    try {
      const session = await api.session();
      if (session.authenticated && session.customer) {
        setCustomer(session.customer);
        if (draftId || guestSpeciesId) {
          setState("ready");
          return;
        }
        const [catalog, draftList] = await Promise.all([api.species(), api.drafts()]);
        setSpecies(sortSpecies(catalog.items));
        setDrafts(draftList.items);
        setState("ready");
        return;
      }
      setCustomer(null);
      if (draftId || guestSpeciesId) {
        setState("ready");
        return;
      }
      const catalog = await api.publicSpecies();
      setSpecies(sortSpecies(catalog.items));
      setDrafts([]);
      setState("ready");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The Character Builder could not be loaded.");
      setState("error");
    }
  }

  async function createCharacter(item: SpeciesItem) {
    if (!item.is_selectable) return;
    if (!authenticated) {
      window.location.assign(`/guest-builder/${encodeURIComponent(item.id)}`);
      return;
    }
    setBusySpecies(item.id);
    setMessage(null);
    try {
      const result = await api.createDraft(item.build_package_id);
      window.location.assign(`/builder/${result.draftId}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The character could not be created.");
      setBusySpecies(null);
    }
  }

  async function deleteDraft(draft: CharacterDraftSummary) {
    const speciesName = speciesNameById.get(draft.species_id) || "this character";
    if (!window.confirm(`Delete the ${speciesName} draft? This cannot be undone.`)) return;
    setBusyDraft(draft.id);
    setMessage(null);
    try {
      await api.abandonDraft(draft.id);
      setDrafts((current) => current.filter((item) => item.id !== draft.id));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The draft could not be deleted.");
    } finally {
      setBusyDraft(null);
    }
  }

  const loginHref = `/auth/shopify/login?returnTo=${encodeURIComponent(SHOPIFY_BUILDER_URL)}`;
  const logoutHref = "/auth/shopify/logout-redirect";

  if (state === "loading") return <main className="center"><div className="seal" aria-hidden="true">妖</div><p>Opening the neighborhood shrine...</p></main>;
  if (state === "error") return <main className="center"><h1>Something went wrong.</h1><p>{message}</p><button className="primary" onClick={() => void load()}>Try again</button></main>;
  if (draftId) return <BuilderMechanical draftId={draftId} />;
  if (guestSpeciesId) return <GuestBuilder speciesId={guestSpeciesId} />;

  return <main className="shell catalog-shell">
    <header className="topbar">
      <div><div className="wordmark">YŌKAI!</div><div className="subtitle">Character Builder</div></div>
      {authenticated
        ? <div className="account"><span>{customer?.displayName}</span><span className="tier">{customer?.accessTier === "core" ? "Core access" : "Quickstart access"}</span><a className="text-button" href={logoutHref} target="_top">Sign out</a></div>
        : <div className="account guest-account"><span>Exploring as a guest</span><a className="primary compact" href={loginHref} target="_top">Sign in</a></div>}
    </header>

    <section className="intro catalog-hero" style={{ display: "block" }}>
      <div>
        <p className="eyebrow">YOUR NEXT INCARNATION</p>
        <h1 style={{ maxWidth: "none" }}>Choose your yōkai.</h1>
        <p>Create a complete, playable character in minutes. Choose a creature, answer a few questions, and let the builder handle the rules.</p>
        <a className="project-link" href={YOKAI_PROJECT_URL} target="_top">New to Yōkai!? Discover the game.</a>
      </div>
    </section>

    {!authenticated && <section className="guest-callout compact-callout"><div><strong>Start free with four playable yōkai.</strong><span>No account or rules knowledge required. Sign in to save characters and unlock any Core content connected to your store account.</span></div><a className="primary compact access-button" href={loginHref} target="_top">Sign in to check access</a></section>}
    {message && <div className="notice" role="alert">{message}</div>}

    <section>
      <div className="section-heading"><h2>Choose your yōkai</h2><span>{selectableCount} available now</span></div>
      <div className="species-grid">{species.map((item) => {
        const locked = !item.is_selectable;
        const artwork = speciesArtUrl(item.id);
        return <article className={`species-card ${locked ? "unavailable locked" : ""}`} key={item.id}>
          <div className="species-art">{artwork ? <img src={artwork} alt="" loading="lazy" /> : <span>ARTWORK</span>}</div>
          {locked && <span className="lock-badge"><LockIcon />Locked</span>}
          <div className="species-copy">
            <div className="species-name-line"><h3>{item.english_name}</h3><span className="species-kanji">{item.kanji || "妖怪"}</span></div>
            {item.kana && <span className="species-kana">{item.kana}</span>}
            <p>{SPECIES_TEASERS[item.id] || "A playable creature rooted in Japanese folklore and adapted to modern Japan."}</p>
            {locked && <small>Login to unlock</small>}
          </div>
          {locked
            ? <a className="secondary species-action" href={loginHref} target="_top">Login to unlock</a>
            : <button className="primary small species-action" disabled={busySpecies !== null} onClick={() => void createCharacter(item)}>{busySpecies === item.id ? "Opening..." : "Create this yōkai"}</button>}
        </article>;
      })}</div>
    </section>

    {authenticated
      ? <section className="draft-section"><div className="section-heading"><h2>Your drafts</h2><span>{drafts.length}</span></div>{drafts.length === 0 ? <div className="empty-state">No saved characters yet. Choose a species to begin.</div> : <div className="draft-list">{drafts.map((draft) => <div className="draft-row" key={draft.id}><a className="draft-link" href={`/builder/${draft.id}`}><div><strong>{speciesNameById.get(draft.species_id) || draft.species_id}</strong><span>Updated {new Date(draft.updated_at).toLocaleDateString("en-US")}</span></div><div><span className="status">{draft.validation_status}</span><span className="continue-label">Continue</span></div></a><button className="delete-draft" type="button" disabled={busyDraft !== null} onClick={() => void deleteDraft(draft)} aria-label={`Delete ${speciesNameById.get(draft.species_id) || "character"} draft`} title="Delete draft"><TrashIcon /></button></div>)}</div>}</section>
      : <section className="guest-footer-cta"><h2>Save your legend.</h2><p>Sign in to store characters online, return to them later, and unlock any Core species included with your account.</p><a className="primary" href={loginHref} target="_top">Sign in with your store account</a></section>}
  </main>;
}
